import {
  clearAnalyticsUser,
  setAnalyticsUser,
  setAnalyticsUserProperties
} from '@cw-elements/analytics-provider';
import { useEffect } from 'react';

import { useAuth } from '@/libs';
import { clearDatadogLogsUser, setDatadogLogsUser } from '@/libs/datadog';

import {
  CONSENT_COOKIE_NAME,
  CONSENT_QUERY_PARAMETER,
  CONSENT_READY_EVENT,
  GRANTED_CONSENT_VALUE,
  UNKNOWN_COUNTRY_CODE
} from './constants';

interface CookiebotWindow extends Window {
  Cookiebot?: {
    consent?: {
      statistics?: boolean;
    };
  };
}

/**
 * Sole owner of the analytics identity, app-wide. The analytics provider
 * applies it to Amplitude and Datadog RUM alike and gates it on the user's
 * cookie consent: while consent is not granted an anonymous per-tab id is
 * reported instead, and the attributes below are held rather than sent — they
 * are never attached to the anonymous identity. The separate Logs SDK follows
 * the same consent decision by only receiving an identity while consent is
 * granted.
 */
const AnalyticsUserIdentifier = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      clearAnalyticsUser();
      clearDatadogLogsUser();

      return;
    }

    setAnalyticsUser(user.id);
    setAnalyticsUserProperties({
      email: user.email,
      name: user.name,
      role: user.role,
      locale: user.locale,
      countryCode: user.country_code || UNKNOWN_COUNTRY_CODE,
      // job_title is optional on the user; user properties only accept
      // strings, so include the key only when it is present.
      ...(user.job_title ? { jobTitle: user.job_title } : {})
    });

    const syncDatadogLogsIdentity = (consentChanged = false) => {
      const consentQueryValue = new URLSearchParams(window.location.search).get(
        CONSENT_QUERY_PARAMETER
      );
      const consentCookieValue = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(`${CONSENT_COOKIE_NAME}=`))
        ?.slice(CONSENT_COOKIE_NAME.length + 1);
      const cookiebotConsent = (window as CookiebotWindow).Cookiebot?.consent;
      let consentGranted: boolean;

      if (consentQueryValue === 'true') {
        consentGranted = true;
      } else if (consentQueryValue === 'false') {
        consentGranted = false;
      } else if (consentChanged && cookiebotConsent) {
        consentGranted = cookiebotConsent.statistics === true;
      } else if (consentCookieValue) {
        consentGranted = consentCookieValue === GRANTED_CONSENT_VALUE;
      } else {
        consentGranted = cookiebotConsent?.statistics === true;
      }

      if (consentGranted) {
        setDatadogLogsUser(user.id);
      } else {
        clearDatadogLogsUser();
      }
    };

    syncDatadogLogsIdentity();
    const handleConsentChange = () => syncDatadogLogsIdentity(true);
    window.addEventListener(CONSENT_READY_EVENT, handleConsentChange);

    return () => {
      window.removeEventListener(CONSENT_READY_EVENT, handleConsentChange);
    };
  }, [user]);

  return null;
};

export default AnalyticsUserIdentifier;
